import { PhotoStudioAIMaskScope } from 'constants/visual/photoStudio'
import { PhotoStudioGenerationStepId } from '../generationSteps'
import { VisualStagingSettings } from 'dataQueries/photoStudio.query'
import { useCallback } from 'react'
import { logger } from 'utils/logging'
import { useReusableMaskUpload } from './useReusableMaskUpload.hook'
import { useVisualStagingProcessor } from './useVisualStagingProcessor.hook'

interface UseVisualStagingFlowProps {
  assignmentId: string
  sourceVisualFilename: string
  getLatestPreviewFilename: () => string | null
  onProgressUpdate?: (step: string) => void
  onComplete?: (resultUrl: string) => void
  onError?: (error: any) => void
}

/**
 * Complete Visual Staging flow hook
 * Combines mask upload and processing into a single reusable flow
 * 
 * Usage:
 * 1. Call uploadMaskAndProcess with mask file and settings
 * 2. Hook handles upload, processing, polling, and result retrieval
 * 3. Calls onComplete with result URL when finished
 */
export const useVisualStagingFlow = ({
  assignmentId,
  sourceVisualFilename,
  getLatestPreviewFilename,
  onProgressUpdate,
  onComplete,
  onError
}: UseVisualStagingFlowProps) => {
  // Reusable mask upload functionality
  const uploadMask = useReusableMaskUpload(assignmentId, PhotoStudioAIMaskScope.VISUAL_STAGING)

  // Visual Staging processor
  const { processVisualStaging, isProcessing, error } = useVisualStagingProcessor({
    assignmentId,
    sourceVisualFilename,
    getLatestPreviewFilename,
    onProgressUpdate,
    onComplete,
    onError
  })

  const uploadMaskAndProcess = useCallback(async (
    maskedImageFile: File | undefined,
    settings: VisualStagingSettings,
    previousJobId?: string
  ): Promise<string> => {
    try {
      onProgressUpdate?.(PhotoStudioGenerationStepId.UPLOADING_MASK)

      // If no mask file provided (entire_image mode), process directly without mask upload
      if (!maskedImageFile) {
        const result = await processVisualStaging(
          undefined,
          settings,
          true, // USE temp bucket for the VirtualStaging!!!
          previousJobId
        )

        if (result.success) {
          return result.jobId!
        } else {
          throw result.error
        }
      }

      // Step 1 & 2: Upload mask file using reusable functionality
      return await new Promise<string>((resolve, reject) => {
        uploadMask.uploadFiles([maskedImageFile], {
          onSettled: async (successIds, errorIds) => {
            if (successIds.length > 0) {
              const maskFilename = successIds[0]
              onProgressUpdate?.(PhotoStudioGenerationStepId.MASK_UPLOADED)

              // Step 3-7: Process Visual Staging using reusable processor
              const result = await processVisualStaging(
                maskFilename,
                settings,
                true, // USE the temp bucket for the VirtualStaging!!!
                previousJobId
              )

              if (result.success) {
                resolve(result.jobId!) // Return the job ID
              } else {
                reject(result.error)
              }
            } else if (errorIds.length > 0) {
              const error = new Error('Mask upload failed')
              onError?.(error)
              reject(error)
            }
          }
        })
      })
    } catch (error) {
      logger.error('Visual Staging: Flow failed', error, {
        context: {
          component: 'PhotoStudio',
          action: 'visualStagingFlow',
          metadata: { assignmentId }
        }
      })
      onError?.(error)
      throw error
    }
  }, [uploadMask, processVisualStaging, onProgressUpdate, onError, assignmentId])

  return {
    uploadMaskAndProcess,
    isProcessing: isProcessing || uploadMask.isAnyActionRunning,
    error
  }
}
